"use client";

import { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";

/**
 * App Router catch-all that fires when an error escapes the root layout
 * itself. Route-level `error.tsx` boundaries cannot catch root-layout
 * failures, so this file is the last line of defence — and the only place
 * where Sentry can hear about a layout-level crash.
 *
 * Constraints that drive the shape of this file:
 *   - It REPLACES the root layout, so it must render its own `<html>` and
 *     `<body>` (otherwise React 19 throws on missing host elements).
 *   - The next-intl provider is set up in the root layout that just failed,
 *     so we cannot call `useTranslations()` here. Strings are intentionally
 *     hard-coded English; a localised fallback would imply we trust state
 *     we already know is broken.
 *   - No imports from `@/components/...` because those pull global styles
 *     and providers that may have caused the crash. The whole point is a
 *     minimum-dependency safety net.
 */
export default function GlobalError({ error }: { error: Error & { digest?: string } }) {
    useEffect(() => {
        Sentry.captureException(error);
    }, [error]);

    return (
        <html lang="en">
            <body
                style={{
                    margin: 0,
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    color: "#0A0D1E",
                    background: "linear-gradient(135deg, #1D2033 0%, #2A2F4F 50%, #1D2033 100%)",
                }}
            >
                <main
                    style={{
                        maxWidth: 480,
                        margin: "0 auto",
                        padding: "32px",
                        textAlign: "center",
                        background: "rgba(255, 255, 255, 0.96)",
                        borderRadius: 16,
                        boxShadow: "0 20px 50px -12px rgba(10, 13, 30, 0.5)",
                    }}
                >
                    <h1 style={{ fontSize: 24, fontWeight: 600, margin: "0 0 8px 0" }}>Something went wrong</h1>
                    <p style={{ fontSize: 16, color: "#5C6275", margin: "0 0 24px 0" }}>An unexpected error occurred. Our team has been notified.</p>
                    <button
                        type="button"
                        onClick={() => window.location.reload()}
                        style={{
                            cursor: "pointer",
                            padding: "10px 20px",
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#FFFFFF",
                            background: "#1D2033",
                            border: "none",
                            borderRadius: 8,
                        }}
                    >
                        Reload page
                    </button>
                </main>
            </body>
        </html>
    );
}
